import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "../../api/api";
import { toast } from "react-toastify";

// List of social media platforms with their Font Awesome icons
const socialMediaIcons = [
  { label: "Facebook", value: "fa-brands fa-facebook" },
  { label: "Twitter", value: "fa-brands fa-twitter" },
  { label: "Instagram", value: "fa-brands fa-instagram" },
  { label: "LinkedIn", value: "fa-brands fa-linkedin-in" },
  { label: "YouTube", value: "fa-brands fa-youtube" },
  { label: "Pinterest", value: "fa-brands fa-pinterest-p" },
  { label: "GitHub", value: "fa-brands fa-github" },
  { label: "WhatsApp", value: "fa-brands fa-whatsapp" },
];

const fieldConfigs = {
  name: {
    type: "name",
    label: "Team Member Name",
    placeholder: "Enter team member name",
    isTextarea: false,
  },
  position: {
    type: "position",
    label: "Position",
    placeholder: "Enter position (e.g., Senior Guide)",
    isTextarea: false,
  },
  image: {
    type: "image",
    label: "Team Member Image",
    placeholder: "Enter image URL",
    isTextarea: false,
  },
  socialLinks: {
    type: "socialLinks",
    label: "Social Links",
    placeholder: "Add social links below",
    isTextarea: false,
    hasChildren: true,
  },
};

const SocialLinkInput = ({ id, index, removeLink, data = {} }) => {
  return (
    <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-md text-gray-800 dark:text-gray-200">
          Social Link {index + 1}
        </span>
        <button
          type="button"
          onClick={removeLink}
          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
        >
          Remove
        </button>
      </div>
      <div className="space-y-2">
        <select
          id={`icon-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          defaultValue={data.icon || ""}
          required
          aria-label="Select social media icon"
        >
          <option value="" disabled>
            Select a social media icon
          </option>
          {socialMediaIcons.map((icon) => (
            <option key={icon.value} value={icon.value}>
              {icon.label}
            </option>
          ))}
        </select>
        <input
          type="url"
          id={`href-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter link URL (e.g., https://facebook.com)"
          defaultValue={data.href || ""}
          required
        />
      </div>
    </div>
  );
};

const FieldInput = ({
  id,
  type,
  label,
  index,
  placeholder,
  isTextarea,
  removeField,
  dragListeners,
  depth,
  data,
  addSubField,
  children,
  allFieldsItems,
  teamMemberIndex,
}) => {
  const fieldName =
    type === "image" ? `image-team-member-${teamMemberIndex}` : `${type}-${id}`;

  return (
    <div
      className={`mb-4 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm ${
        depth > 0 ? "border-primary-500" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <div {...dragListeners} className="cursor-grab mr-2 text-gray-500">
            ≡
          </div>
          <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
            {label}
          </span>
        </div>
        <button
          type="button"
          onClick={removeField}
          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
        >
          Remove
        </button>
      </div>
      {type === "image" ? (
        <>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            URL (optional)
          </label>
          <input
            type="url"
            id={`url-${type}-${id}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
            placeholder={`${placeholder} (optional)`}
            defaultValue={data && !data.startsWith("/Uploads/") ? data : ""}
          />
          <div className="mt-3">
            <label
              htmlFor={fieldName}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Or Upload File (required if no URL)
            </label>
            <input
              type="file"
              accept="image/*"
              id={fieldName}
              name={fieldName}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200"
            />
          </div>
          {data && data.startsWith("/Uploads/") && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Preview
              </label>
              <img
                src={`${process.env.REACT_APP_BACKEND_API_URL}${data}`}
                alt="Preview"
                style={{ maxWidth: "200px" }}
                onError={(e) => (e.target.src = "/fallback-image.png")}
              />
            </div>
          )}
        </>
      ) : type === "socialLinks" && fieldConfigs[type].hasChildren ? (
        <>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {placeholder}
          </p>
          {Array.isArray(children) && children.length > 0 ? (
            <SortableContext
              id={`sortable-${id}`}
              items={children.map((child) => child.id)}
              strategy={verticalListSortingStrategy}
            >
              {children.map((child, idx) => (
                <SocialLinkInput
                  key={child.id}
                  id={child.id}
                  index={idx}
                  removeLink={() => removeField(child.id)}
                  data={child.data || {}}
                />
              ))}
            </SortableContext>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 mb-2">
              No social links added yet.
            </p>
          )}
          <button
            type="button"
            onClick={() =>
              addSubField(id, {
                id: crypto.randomUUID(),
                type: "socialLink",
                data: {},
              })
            }
            className="mt-2 px-4 py-2 bg-transparent border border-primary-500 text-primary-500 rounded-md hover:bg-primary-500 hover:text-white"
          >
            Add Social Link
          </button>
        </>
      ) : isTextarea ? (
        <textarea
          id={`${type}-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data || ""}
          required
        />
      ) : (
        <input
          type="text"
          id={`${type}-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data || ""}
          required
        />
      )}
    </div>
  );
};

const TeamMemberItem = ({
  teamMember,
  index,
  removeTeamMember,
  allFieldsItems,
  activeId,
  projected,
  addSubField,
  removeField,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: teamMember.id,
    });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="mb-6 p-6 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-md"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div {...listeners} className="cursor-grab mr-2 text-gray-500">
            ≡
          </div>
          <span className="font-semibold text-xl text-gray-800 dark:text-gray-200">
            Team Member {index + 1}
          </span>
        </div>
        <button
          type="button"
          onClick={() => removeTeamMember(teamMember.id)}
          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
        >
          Remove Team Member
        </button>
      </div>
      <SortableContext
        id={`sortable-team-${teamMember.id}`}
        items={teamMember.children.map((child) => child.id)}
        strategy={verticalListSortingStrategy}
      >
        {teamMember.children.map((item, idx) => (
          <FieldInput
            key={item.id}
            id={item.id}
            type={item.type}
            label={item.label}
            index={idx}
            placeholder={item.placeholder}
            isTextarea={item.isTextarea}
            removeField={() => removeField(item.id)}
            dragListeners={{}}
            depth={1}
            data={item.data}
            addSubField={addSubField}
            children={item.children}
            allFieldsItems={allFieldsItems}
            teamMemberIndex={index}
          />
        ))}
      </SortableContext>
    </div>
  );
};

function flattenTree(items, parent = null, depth = 0) {
  return items.reduce((acc, item) => {
    const newItem = { ...item, parent, depth };
    acc.push(newItem);
    if (item.children && item.children.length > 0) {
      acc.push(...flattenTree(item.children, item.id, depth + 1));
    }
    return acc;
  }, []);
}

function buildTree(flattenedItems) {
  const idMapping = flattenedItems.reduce((acc, item) => {
    acc[item.id] = {
      id: item.id,
      type: item.type,
      label: item.label,
      placeholder: item.placeholder,
      isTextarea: item.isTextarea,
      data: item.data,
      children: [],
    };
    return acc;
  }, {});

  const root = [];

  flattenedItems.forEach((item) => {
    const node = idMapping[item.id];
    if (item.parent === null) {
      root.push(node);
    } else {
      const parentNode = idMapping[item.parent];
      if (parentNode) {
        parentNode.children.push(node);
      }
    }
  });

  return root;
}

function getProjection(
  items,
  activeId,
  overId,
  dragOffset,
  indentationWidth = 20,
) {
  const overItemIndex = items.findIndex((i) => i.id === overId);
  const activeItemIndex = items.findIndex((i) => i.id === activeId);

  if (overItemIndex === -1 || activeItemIndex === -1) return null;

  const activeItem = items[activeItemIndex];
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];

  const dragDepth = Math.round(dragOffset / indentationWidth);
  const tentativeDepth = Math.max(0, activeItem.depth + dragDepth);

  const maxDepth = previousItem ? previousItem.depth + 1 : 0;
  const minDepth = nextItem ? nextItem.depth : 0;

  let depth = Math.max(minDepth, Math.min(maxDepth, tentativeDepth));

  let parentId;
  if (depth === 0) {
    parentId = null;
  } else {
    let current = previousItem;
    let currentIndex = overItemIndex - 1;
    while (current && current.depth >= depth && currentIndex >= 0) {
      currentIndex -= 1;
      current = newItems[currentIndex];
    }
    parentId = current ? current.id : null;
  }

  return { depth, maxDepth, minDepth, parentId };
}

function removeFromTree(items, id) {
  return items
    .map((item) => {
      if (item.id === id) {
        return null; // Mark the specific item for removal
      }
      if (item.children && item.children.length > 0) {
        // Process children and remove only the specific id
        item.children = removeFromTree(item.children, id);
        // Filter out null children to preserve the parent structure
        item.children = item.children.filter((child) => child !== null);
      }
      return item;
    })
    .filter((item) => item !== null); // Remove null items at the top level
}

function getAllFields(items) {
  return items.reduce(
    (acc, item) => [...acc, item, ...getAllFields(item.children || [])],
    [],
  );
}

function hydrateContent(content, generateId) {
  if (!Array.isArray(content)) return [];
  return content.map((member) => ({
    id: generateId(),
    type: "teamMember",
    label: "Team Member",
    children: [
      {
        id: generateId(),
        ...fieldConfigs.name,
        data: member.name || "",
      },
      {
        id: generateId(),
        ...fieldConfigs.position,
        data: member.position || "",
      },
      {
        id: generateId(),
        ...fieldConfigs.image,
        data: member.image || "",
      },
      {
        id: generateId(),
        ...fieldConfigs.socialLinks,
        children: (member.socialLinks || []).map((link) => ({
          id: generateId(),
          type: "socialLink",
          data: { icon: link.icon, href: link.href },
        })),
      },
    ],
  }));
}

export default function AddTeamMembers({
  isEdit = false,
  sectionId = null,
  onSuccess = () => {},
}) {
  const generateId = () =>
    crypto.randomUUID?.() || Math.random().toString(36).substring(2, 9);
  const [fields, setFields] = useState({ allFields: [] });
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [projected, setProjected] = useState(null);
  const [loading, setLoading] = useState(false);

  const flattenedFields = useMemo(
    () => flattenTree(fields.allFields),
    [fields.allFields],
  );
  const allFieldsItems = useMemo(
    () => getAllFields(fields.allFields),
    [fields.allFields],
  );

  useEffect(() => {
    if (isEdit && sectionId) {
      fetchSectionById(sectionId);
    } else {
      // Initialize with one team member containing dummy data
      addTeamMember();
    }
  }, [isEdit, sectionId]);

  const fetchSectionById = async (id) => {
    setLoading(true);
    try {
      const resp = await axios.get(`/api/tour-guide/${id}`);
      const sectionObj = resp?.data?.data || resp?.data;
      if (sectionObj && sectionObj.teamMembers) {
        const hydrated = hydrateContent(sectionObj.teamMembers, generateId);
        setFields({ allFields: hydrated });
      } else {
        toast.error("Invalid section data to edit");
      }
    } catch (err) {
      console.error("Error fetching section:", err);
      toast.error("Failed to load section data");
    } finally {
      setLoading(false);
    }
  };

  const addTeamMember = () => {
    const newId = generateId();
    const newTeamMember = {
      id: newId,
      type: "teamMember",
      label: "Team Member",
      children: [
        {
          id: generateId(),
          ...fieldConfigs.name,
          data: "John Doe", // Dummy name
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.position,
          data: "Senior Guide", // Dummy position
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.image,
          // data: "https://via.placeholder.com/150",
          children: [],
        },
        {
          id: generateId(),
          ...fieldConfigs.socialLinks,
          data: "",
          children: [
            {
              id: generateId(),
              type: "socialLink",
              data: {
                icon: "fa-facebook-f",
                href: "https://facebook.com/johndoe",
              },
            },
            {
              id: generateId(),
              type: "socialLink",
              data: {
                icon: "fa-twitter",
                href: "https://twitter.com/johndoe",
              },
            },
          ],
        },
      ],
    };
    setFields((prev) => ({ allFields: [...prev.allFields, newTeamMember] }));
  };

  const removeTeamMember = (id) => {
    setFields((prev) => ({
      allFields: removeFromTree(prev.allFields, id),
    }));
  };

  const removeField = (id) => {
    setFields((prev) => ({
      allFields: removeFromTree(prev.allFields, id),
    }));
  };

  const addSubField = (parentId, newField) => {
    setFields((prev) => {
      const updateTree = (items) =>
        items.map((item) =>
          item.id === parentId
            ? { ...item, children: [...(item.children || []), newField] }
            : { ...item, children: updateTree(item.children || []) },
        );
      return { allFields: updateTree(prev.allFields) };
    });
  };

  const areAllFieldsRemoved = () => fields.allFields.length === 0;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    setOverId(active.id);
    setOffsetLeft(0);
  };

  const handleDragMove = ({ delta, over }) => {
    setOffsetLeft(delta.x);
    if (over) {
      setOverId(over.id);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (projected && over) {
      const { depth, parentId } = projected;
      const cloned = [...flattenedFields];
      const overIndex = cloned.findIndex((i) => i.id === over.id);
      const activeIndex = cloned.findIndex((i) => i.id === active.id);
      const activeTreeItem = cloned[activeIndex];
      cloned[activeIndex] = { ...activeTreeItem, depth, parent: parentId };
      const newItems = arrayMove(cloned, activeIndex, overIndex);
      setFields((prev) => ({
        allFields: buildTree(newItems),
      }));
    }
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  useEffect(() => {
    if (activeId && overId) {
      setProjected(
        getProjection(flattenedFields, activeId, overId, offsetLeft, 20),
      );
    } else {
      setProjected(null);
    }
  }, [activeId, overId, offsetLeft, flattenedFields]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    let hasError = false;
    const teamMembersArray = fields.allFields.map((teamMember, index) => {
      if (teamMember.type !== "teamMember") return null;
      const memberData = {};
      teamMember.children.forEach((field) => {
        if (["name", "position"].includes(field.type)) {
          const el = document.getElementById(`${field.type}-${field.id}`);
          if (!el || !el.value?.trim()) {
            toast.error(
              `${field.label} cannot be empty for Team Member ${index + 1}`,
            );
            hasError = true;
            return;
          }
          memberData[field.type] = el.value.trim();
        } else if (field.type === "image") {
          const urlEl = document.getElementById(
            `url-${field.type}-${field.id}`,
          );
          const fileInput = document.getElementById(
            `image-team-member-${index}`,
          );
          const url = urlEl?.value?.trim();
          if (url) {
            memberData.image = url;
          } else if (fileInput?.files?.[0]) {
            formData.append(`image-team-member-${index}`, fileInput.files[0]);
            memberData.image = `image-team-member-${index}`;
          } else if (field.data && field.data.startsWith("/Uploads/")) {
            memberData.image = field.data;
          } else {
            memberData.image = "";
          }
        } else if (field.type === "socialLinks") {
          memberData.socialLinks = (field.children || []).map((child) => {
            const icon = document
              .getElementById(`icon-${child.id}`)
              ?.value?.trim();
            const href = document
              .getElementById(`href-${child.id}`)
              ?.value?.trim();
            if (!icon || !href) {
              toast.error(
                `Social link icon and URL are required for Team Member ${index + 1}`,
              );
              hasError = true;
            }
            return { icon, href };
          });
        }
      });
      return memberData;
    });

    if (hasError) return;

    if (!teamMembersArray.length) {
      toast.error("At least one team member is required");
      return;
    }

    formData.append("teamMembers", JSON.stringify(teamMembersArray));
    formData.append("date", new Date().toISOString());

    try {
      let resp;
      if (isEdit) {
        resp = await axios.put(`/api/tour-guide/${sectionId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Team members updated successfully!");
      } else {
        resp = await axios.post("/api/tour-guide/add-section", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Team members added successfully!");
      }
      setFields({ allFields: [] });
      onSuccess();
    } catch (err) {
      console.error("Submit error:", err.response?.data || err.message);
      toast.error(
        `Failed to ${isEdit ? "update" : "add"} team members: ${
          err.response?.data?.message || err.message
        }`,
      );
    }
  };

  const activeItem = activeId
    ? flattenedFields.find((i) => i.id === activeId)
    : null;

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24 duration-300">
        <div className="xl:w-full min-h-[calc(100vh-152px)] relative pb-14">
          <div className="grid md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12 gap-6 mb-6">
            <div className="sm:col-span-12 md:col-span-12 lg:col-span-8 xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg w-full relative mb-6 shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 dark:text-gray-300">
                  <h4 className="font-semibold text-xl flex-1 self-center mb-2 md:mb-0">
                    {isEdit ? "Edit Team Members" : "Add Team Members"}
                  </h4>
                </div>
                <form onSubmit={handleSubmit} className="flex-auto p-6">
                  {loading ? (
                    <div className="space-y-4">
                      {Array(5)
                        .fill()
                        .map((_, idx) => (
                          <div
                            key={idx}
                            className="animate-pulse bg-gray-200 dark:bg-gray-700 h-16 rounded-md"
                          ></div>
                        ))}
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCorners}
                      onDragStart={handleDragStart}
                      onDragMove={handleDragMove}
                      onDragEnd={handleDragEnd}
                      onDragCancel={handleDragCancel}
                    >
                      <SortableContext
                        id="root"
                        items={fields.allFields.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {areAllFieldsRemoved() ? (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            No team members have been added. Please add a team
                            member using the button below.
                          </p>
                        ) : (
                          fields.allFields.map((teamMember, index) => (
                            <TeamMemberItem
                              key={teamMember.id}
                              teamMember={teamMember}
                              index={index}
                              removeTeamMember={removeTeamMember}
                              allFieldsItems={allFieldsItems}
                              activeId={activeId}
                              projected={projected}
                              addSubField={addSubField}
                              removeField={removeField}
                            />
                          ))
                        )}
                      </SortableContext>
                      <DragOverlay>
                        {activeId && activeItem ? (
                          activeItem.type === "teamMember" ? (
                            <div className="p-6 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-md">
                              <span className="font-semibold text-xl text-gray-800 dark:text-gray-200">
                                {activeItem.label}
                              </span>
                            </div>
                          ) : (
                            <FieldInput
                              id={activeItem.id}
                              type={activeItem.type}
                              label={activeItem.label}
                              index={null}
                              placeholder={activeItem.placeholder}
                              isTextarea={activeItem.isTextarea}
                              removeField={() => {}}
                              dragListeners={{}}
                              depth={
                                projected ? projected.depth : activeItem.depth
                              }
                              data={activeItem.data}
                              addSubField={addSubField}
                              children={activeItem.children}
                              allFieldsItems={allFieldsItems}
                              teamMemberIndex={0}
                            />
                          )
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}
                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                      disabled={areAllFieldsRemoved() || loading}
                    >
                      {isEdit ? "Update" : "Submit"}
                    </button>
                    <button
                      type="button"
                      className="ml-3 px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      onClick={() => setFields({ allFields: [] })}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
                <div className="p-6 pt-0">
                  <div className="mt-8">
                    <h5 className="font-semibold text-base text-gray-700 dark:text-gray-300 mb-3">
                      Add Team Member
                    </h5>
                    <button
                      onClick={addTeamMember}
                      className="px-4 py-2 bg-transparent border border-primary-500 text-primary-500 rounded-md hover:bg-primary-500 hover:text-white"
                    >
                      Add Team Member
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
